import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import { useSetAtom, useAtomValue } from 'jotai';
import { GenericModal } from '@/components/shared/GenericModal';
import { AutoCompleteOption, GenericModalProps } from '@/types';
import { dialogOverlay } from '@/components/UserManagement/styles';
import { VisibilityEnum } from '@/__generated__/graphql';
import { AbyssAutoComplete } from '@/components/shared/AbyssAutoComplete';
import { useGetAllTeamsForCreateOrEditPoiQuery } from '@/__generated__/graphql';
import { useUpdatePointOfInterestVisibilityMutation } from '@/__generated__/graphql';
import { PointOfInterestsDocument } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { LoadingButton } from '@/components/shared/LoadingButton';
import { useGetStructureId } from '@/hooks/useGetStructureId';

type Props = GenericModalProps & {
  selectedPointOfInterestIds: string[];
  clearSelectedPOIs: () => void;
  isBulkUpdate: boolean;
  paintRegionIds?: string[];
  selectedPoiFilters: { id: string; value: string[] }[];
  type: string;
};

const UpdatePoiVisibility = ({
  open,
  handleClose,
  title,
  selectedPointOfInterestIds,
  clearSelectedPOIs,
  isBulkUpdate,
  paintRegionIds = [],
  selectedPoiFilters,
  type,
}: Props) => {
  const [poiVisibility, setPoiVisibility] = useState<VisibilityEnum>(VisibilityEnum.Private);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const setSnackbarMessage = useSetAtom(state.snackbarMessage);

  const structureId = useGetStructureId();
  const selectedTemplateFilters = useAtomValue(state.selectedTemplateFiltersState);

  const { data, loading: teamsLoading } = useGetAllTeamsForCreateOrEditPoiQuery({
    skip: !open,
    fetchPolicy: 'cache-first',
  });
  const allTeams: AutoCompleteOption[] = useMemo(() => {
    const teams = data?.allTeams ?? [];
    return teams.map((team) => ({
      label: team.name,
      value: team.id,
    }));
  }, [data?.allTeams]);

  const handleVisibilityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPoiVisibility(event.target.value as VisibilityEnum);
  };

  const handleClearModal = useCallback(() => {
    handleClose();
    setSelectedTeamIds([]);
    setPoiVisibility(VisibilityEnum.Private);
  }, [handleClose]);

  const [updatePointOfInterestVisibilityMutation, { loading }] =
    useUpdatePointOfInterestVisibilityMutation();

  const handleUpdateVisibility = useCallback(async () => {
    await updatePointOfInterestVisibilityMutation({
      variables: {
        input: {
          pointOfInterestIds: isBulkUpdate ? [] : selectedPointOfInterestIds,
          visibility: poiVisibility,
          teamIds: poiVisibility === VisibilityEnum.Custom ? selectedTeamIds : [],
          isBulkUpdate,
          lastModifiedFor: 'Update Visibility',
        },
        filters: {
          paintRegionIds,
          selectedTemplateFilters,
          selectedPoiFilters,
          structureId,
          type,
        },
      },
      refetchQueries: [PointOfInterestsDocument],
      onCompleted: () => {
        setSnackbarMessage({
          shouldShow: true,
          content: (
            <Alert severity="success">Visibility of POIs have been successfully updated</Alert>
          ),
        });
      },
      onError: (error) => {
        setSnackbarMessage({
          shouldShow: true,
          content: <Alert severity="error">{error.message}</Alert>,
        });
      },
    });
    clearSelectedPOIs();
    handleClearModal();
  }, [
    clearSelectedPOIs,
    handleClearModal,
    isBulkUpdate,
    paintRegionIds,
    poiVisibility,
    selectedTemplateFilters,
    selectedPoiFilters,
    selectedPointOfInterestIds,
    selectedTeamIds,
    setSnackbarMessage,
    structureId,
    type,
    updatePointOfInterestVisibilityMutation,
  ]);

  enum POIVisibilityLevels {
    Private = 'PRIVATE',
    Public = 'PUBLIC',
    Custom = 'CUSTOM',
  }

  return (
    <GenericModal
      open={open}
      handleClose={handleClose}
      title={title}
      headerStyleProp={{ mx: '2%' }}
      sx={dialogOverlay}
    >
      <Box width="100%" sx={{ px: '35px' }} data-testid="update-poi-bulk-visibility">
        <RadioGroup
          row
          value={poiVisibility}
          onChange={handleVisibilityChange}
          sx={{ justifyContent: 'space-between' }}
        >
          {Object.entries(POIVisibilityLevels).map(([key, value]) => (
            <FormControlLabel value={value} control={<Radio />} label={key} key={value} />
          ))}
        </RadioGroup>
        <Divider sx={{ mt: 3 }} />
        <Box sx={{ my: 3 }}>
          {poiVisibility !== VisibilityEnum.Custom && (
            <Stack
              direction="row"
              sx={{ cursor: 'pointer', mt: 3 }}
              spacing={2}
              alignItems="center"
            >
              <Stack direction="column">
                <Typography variant="subtitle1">
                  {poiVisibility === VisibilityEnum.Private
                    ? "Note: These POI's are only visible to creator and admin."
                    : "Note: These POI's are visible to all Abyss fabric users."}
                </Typography>
              </Stack>
            </Stack>
          )}
          {poiVisibility === VisibilityEnum.Custom && (
            <>
              <AbyssAutoComplete
                variant="standard"
                label="Select Teams"
                loading={teamsLoading}
                options={allTeams}
                selectedValues={selectedTeamIds}
                handleSelected={setSelectedTeamIds}
                error={selectedTeamIds.length === 0}
                helperText={selectedTeamIds.length === 0 ? 'Please select at least one team' : ''}
              />
              {selectedTeamIds.length > 0 && (
                <Typography variant="subtitle1" sx={{ mt: 1 }}>
                  {`Note: These POI's are visible to creator and admin, in addition to the teams mentioned above.`}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
      <DialogActions
        sx={{
          position: 'absolute',
          bottom: 0,
          borderTop: 1,
          borderColor: 'divider',
          marginTop: 'auto',
          width: '100%',
        }}
      >
        <Button
          id="poi-visiblity-cancel"
          onClick={handleClearModal}
          variant="text"
          sx={{ ml: 'auto', mr: 1, height: '40px' }}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          onClick={handleUpdateVisibility}
          disabled={poiVisibility === VisibilityEnum.Custom && selectedTeamIds.length === 0}
          buttonStyles={{ height: '40px' }}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </GenericModal>
  );
};

export default UpdatePoiVisibility;
